"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import { useInformation } from "@/context/InformationContext";
import { isUnauthenticatedOnlyRoute } from "@/constants/routeGuards";

/**
 * Global client-side auth redirect.
 *
 * Redirects authenticated users away from unauthenticated-only pages
 * (see `UNAUTHENTICATED_ONLY_ROUTES`) to `/dashboard`.
 *
 * Mount this once, at the top of the app tree (ClientLayout).
 *
 * Why client-side and not Next.js middleware?
 * The session token lives in `localStorage`, which edge middleware
 * cannot read. Until auth is migrated to cookies, this hook is the
 * single source of truth for this redirect.
 *
 * Relationship to `ProtectedRoute`:
 * `ProtectedRoute` handles the opposite direction (unauthenticated
 * users hitting protected pages) and also enforces permission-based
 * access. The two guards operate on disjoint route sets.
 */
export const useAuthRedirect = (): void => {
  const { token, user, isAppLoading } = useAuth();
  const { setHasConversations } = useInformation();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (isAppLoading) return;
    if (!token || !user) return;
    if (!isUnauthenticatedOnlyRoute(pathname)) return;

    setHasConversations(false);
    router.replace("/dashboard");
  }, [token, user, isAppLoading, pathname, router, setHasConversations]);
};
